import { css, Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { scrollbarStyles } from 'common/styledComponents/mixins';
import { Text } from 'common/styledComponents/typography';
import { IconItemProps } from 'common/types/common';

interface ActiveColorProps {
  theme: Theme;
  active?: boolean;
  noActiveDefaultText?: boolean;
  color?: string;
  fontWeight?: string;
}

/** Resolves the text colour and font-weight shared by SelectedItem / SelectedItemInnerContainer. */
const activeColorMixin = ({ theme, active, noActiveDefaultText, color, fontWeight = '600' }: ActiveColorProps) => css`
  color: ${color ??
  (active ? theme.colors.lightPurple.toString() : noActiveDefaultText ? theme.v2.colours.gray900 : theme.colors.regularGrey.toString())};
  font-weight: ${fontWeight};
`;

export const SelectedItem = styled.div<{
  active?: boolean;
  noHover?: boolean;
  extraPadding?: boolean;
  height?: string;
  padding?: string;
  color?: string;
  noActiveDefaultText?: boolean;
  fontWeight?: string;
  centeredIconTrigger?: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: ${({ centeredIconTrigger }) => (centeredIconTrigger ? 'center' : 'flex-start')};
  border-radius: ${({ theme }) => theme.space(0.5)};
  ${({ height = '28px' }) => `height: ${height};`}
  min-width: 28px;
  padding: ${({ theme, extraPadding, padding }) => (padding ? padding : extraPadding ? theme.space(1) : '0')};

  ${({ noHover, theme, active }) =>
    !noHover &&
    css`
      &:hover {
        background: ${active ? theme.colors.lightPurple.alpha(0.1).toString() : theme.colors.regularGrey.alpha(0.1).toString()};
      }
    `}

  ${activeColorMixin}
`;

const DropDownContainer = styled.div<{
  withBorder: boolean;
  active: boolean;
  borderRadius?: string;
  height?: string;
  borderColor?: string;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.v2.colours.gray900};
  background: ${({ theme }) => theme.v2.colours.gray0};
  cursor: pointer;

  ${({ withBorder, theme, active, borderRadius = '4px', borderColor }) =>
    withBorder &&
    css`
      border: 1px solid ${borderColor ?? (active ? theme.v2.colours.latana100 : theme.v2.colours.gray200)};
      border-radius: ${borderRadius};
    `}
`;

const DropDownListWrapper = styled.div<{ align: string | number }>`
  position: absolute;
  background: ${({ theme }) => theme.colors.white.toString()};
  top: 0px;
  left: ${({ align }) => (align === 'left' ? 0 : 'initial')};
  right: ${({ align }) => (align === 'right' ? 0 : 'initial')};
  z-index: 99;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: ${({ theme }) => `${theme.v2.space(1.5)} ${theme.v2.space(0.5)}`};
  max-width: 600px;
  width: auto;
  gap: 4px;
  display: flex;
  flex-direction: column;
  overflow: auto;
  max-height: 60vh;
  ${scrollbarStyles()}
`;

const DropDownListItem = styled.div<{ hasDescription?: boolean }>`
  color: ${({ theme }) => theme.v2.colours.gray900};
  padding: ${({ theme, hasDescription }) =>
    hasDescription ? `${theme.v2.space(1)} ${theme.v2.space(1.5)}` : `${theme.v2.space(0.5)} ${theme.v2.space(1.5)}`};
  cursor: pointer;
  user-select: none;
  min-height: ${({ hasDescription }) => (hasDescription ? '48px' : '28px')};
  font-weight: 600;
  position: relative;
  display: flex;
  align-items: ${({ hasDescription }) => (hasDescription ? 'flex-start' : 'center')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${({ theme }) => theme.colors.black05.toString()};
  }

  &[aria-disabled='true'] {
    opacity: 0.2;
    &:hover {
      background: none;
    }
  }
`;

const Label = styled(Text)`
  padding: ${({ theme }) => theme.v2.space(0, 1, 1, 1)};
  text-wrap: nowrap;
`;

const IconItem = styled.div<IconItemProps>`
  background: url('${({ icon }) => icon}') no-repeat center center;
  border: none;
  width: 100%;
  height: 100%;
  min-height: 28px;
  min-width: 28px;
  cursor: pointer;
  filter: invert(60%) sepia(0%) saturate(29%) hue-rotate(186deg) brightness(91%) contrast(102%);
  margin-left: ${({ theme, centeredIconTrigger }) => (centeredIconTrigger ? '0' : theme.v2.space(1))};

  ${({ withLabel }) =>
    withLabel &&
    css`
      filter: invert(44%) sepia(49%) saturate(6042%) hue-rotate(242deg) brightness(98%) contrast(107%);
    `}

  ${({ active }) =>
    active &&
    css`
      filter: invert(32%) sepia(71%) saturate(3252%) hue-rotate(245deg) brightness(103%) contrast(101%);
    `}
`;

const LabelWrapper = styled.div`
  margin-bottom: 12px;
`;

const SelectedItemInnerContainer = styled.div<{
  active?: boolean;
  noActiveDefaultText?: boolean;
  color?: string;
  fontWeight?: string;
  centeredIconTrigger?: boolean;
}>`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 195px;
  margin-right: ${({ theme, centeredIconTrigger }) => (centeredIconTrigger ? '0' : theme.v2.space(1))};
  display: flex;
  align-items: center;
  justify-content: ${({ centeredIconTrigger }) => (centeredIconTrigger ? 'center' : 'flex-start')};
  ${activeColorMixin}
`;

const Container = styled.div`
  max-width: 195px;
`;

const RadioButtonCircle = styled.div<{ selected: boolean; hasDescription?: boolean }>`
  width: 16px;
  height: 16px;
  min-width: 16px;
  border: 1px solid ${({ theme, selected }) => (selected ? theme.v2.colours.latana600 : theme.v2.colours.gray600)};
  border-radius: 50%;
  margin-right: ${({ theme }) => theme.v2.space(1)};
  margin-top: ${({ hasDescription }) => (hasDescription ? '2px' : '0')};
  position: relative;
  background-color: ${({ theme, selected }) => (selected ? theme.v2.colours.latana600 : 'transparent')};
  transition: all 0.3s ease;

  &::before {
    content: '';
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: white;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    visibility: ${({ selected }) => (selected ? 'visible' : 'hidden')};
  }
`;

const ListItemLabelWrapper = styled.div`
  flex-grow: 1;
  text-wrap: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.v2.space(0.25)};
`;

const Description = styled(Text)`
  font-size: ${({ theme }) => theme.v2.text.variants.L1.fontSize};
  font-weight: ${({ theme }) => theme.v2.text.variants.L1.fontWeight};
  color: ${({ theme }) => theme.v2.colours.gray400};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

export {
  Description,
  ListItemLabelWrapper,
  RadioButtonCircle,
  Container,
  SelectedItemInnerContainer,
  LabelWrapper,
  Label,
  IconItem,
  DropDownListItem,
  DropDownListWrapper,
  DropDownContainer,
};
